// Thin fetch wrapper. Same-origin only; the session rides in an httpOnly
// cookie the page never sees.

export interface PublicConfig {
  programName: string;
  programShort: string;
  colors: { primary: string; primaryLight: string };
  logoPath: string;
  idleMinutes: number;
  /** live, or test (the test environment, plan §8A). */
  environment: "live" | "test";
  /** SUPPORT_EMAIL, or null: then no help link is shown anywhere. */
  supportEmail: string | null;
}

export interface Me {
  userKey: string;
  name: string;
  email: string;
  /** "+1" and 10 digits, or "". */
  phone: string;
  role: string;
  roleLabel: string;
  credential: string;
  /** How released emails sign ("Director", "RN"); "" = the name alone. */
  signatureTitle: string;
  licensed: boolean;
  mayAdministerOtc: boolean;
  capabilities: string[];
}

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T & { error?: string; code?: string; reason?: string; expired?: boolean };
}

/**
 * When this page last made a request the SERVER counts as activity (anything
 * but a GET ...?peek=1). The idle mirror in idle.ts reads it, so the client's
 * idle clock and the server's agree.
 */
let lastServerActivity = Date.now();
export function lastServerActivityMs(): number {
  return lastServerActivity;
}

export async function api<T = Record<string, unknown>>(
  method: string,
  path: string,
  body?: unknown
): Promise<ApiResult<T>> {
  if (!(method === "GET" && /[?&]peek=1\b/.test(path))) lastServerActivity = Date.now();
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: body !== undefined ? { "content-type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    return {
      ok: false,
      status: 0,
      data: { error: "Can't reach the server. Check the connection, or use the paper packet." } as ApiResult<T>["data"],
    };
  }
  let data: ApiResult<T>["data"];
  try {
    data = await res.json();
  } catch {
    data = { error: "Unexpected response from the server." } as ApiResult<T>["data"];
  }
  return { ok: res.ok, status: res.status, data };
}

/** Sends a file as the raw request body (photos, PDFs); the server checks what it really is. JSON back. */
export async function upload<T = Record<string, unknown>>(path: string, file: Blob): Promise<ApiResult<T>> {
  lastServerActivity = Date.now();
  let res: Response;
  try {
    res = await fetch(path, { method: "POST", credentials: "same-origin", headers: { "content-type": file.type || "application/octet-stream" }, body: file });
  } catch {
    return { ok: false, status: 0, data: { error: "Can't reach the server. Check the connection, or use the paper packet." } as ApiResult<T>["data"] };
  }
  let data: ApiResult<T>["data"];
  try {
    data = await res.json();
  } catch {
    data = { error: res.status === 413 ? "That file is too large." : "Unexpected response from the server." } as ApiResult<T>["data"];
  }
  return { ok: res.ok, status: res.status, data };
}

/** POST, and the answer as a file to save (the provider packet). */
export async function downloadPost(path: string, fallbackName: string): Promise<string | null> {
  lastServerActivity = Date.now();
  let res: Response;
  try {
    res = await fetch(path, { method: "POST", credentials: "same-origin" });
  } catch {
    return "Can't reach the server.";
  }
  if (!res.ok) {
    try {
      return ((await res.json()) as { error?: string }).error ?? "That didn't work.";
    } catch {
      return "That didn't work.";
    }
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return null;
}

/** Applies the program's name, colors and logo at runtime (one build, two programs). */
export function applyBranding(c: PublicConfig): void {
  const root = document.documentElement.style;
  root.setProperty("--program-primary", c.colors.primary);
  root.setProperty("--program-primary-light", c.colors.primaryLight);
  document.title = `${c.programName} Medical Administration`;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", c.colors.primary);
}
